/**
 * API client for the loyalty admin panel.
 * All calls go through authFetch which adds the Shopify session token.
 */

import { authFetch } from "./auth-client.js";

const API_BASE = "/api";

async function apiCall(endpoint, options = {}) {
  const response = await authFetch(`${API_BASE}${endpoint}`, {
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
    ...options,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: response.statusText }));
    throw new Error(error.error || "API error");
  }

  return response.json();
}

// Dashboard
export async function getDashboardStats() {
  return apiCall("/dashboard");
}

// Customers
export async function getCustomers({ tier, q, after, limit } = {}) {
  const params = new URLSearchParams();
  if (tier) params.set("tier", tier);
  if (q) params.set("q", q);
  if (after) params.set("after", after);
  if (limit) params.set("limit", limit);
  return apiCall(`/customers?${params}`);
}

export async function getCustomerDetail(customerId) {
  // customerId may be a numeric or full gid; the endpoint accepts both via [id].js
  const id = customerId.startsWith("gid://")
    ? customerId.split("/").pop()
    : customerId;
  return apiCall(`/customers/${encodeURIComponent(id)}`);
}

export async function adjustPoints(customerId, delta, reason) {
  return apiCall("/customers/adjust-points", {
    method: "POST",
    body: JSON.stringify({ customerId, delta, reason }),
  });
}

// Settings
export async function getSettings() {
  return apiCall("/settings");
}

export async function updateSettings(settings) {
  return apiCall("/settings", {
    method: "POST",
    body: JSON.stringify(settings),
  });
}

// Coupons / Redemption (admin manual issuance)
export async function redeemPoints(customerId, couponTierIndex) {
  return apiCall("/redeem", {
    method: "POST",
    body: JSON.stringify({ customerId, couponTierIndex }),
  });
}

// Badges
export async function getBadges() {
  return apiCall("/badges");
}
export async function saveBadge(badge) {
  return apiCall("/badges", { method: "POST", body: JSON.stringify(badge) });
}

// Challenges
export async function getChallenges() {
  return apiCall("/challenges");
}
export async function saveChallenge(challenge) {
  return apiCall("/challenges", { method: "POST", body: JSON.stringify(challenge) });
}

// Launch Wizard
export async function runTierMigration() {
  return apiCall("/admin/migrate-tiers", { method: "POST" });
}
export async function runRetroactiveGrant() {
  return apiCall("/admin/grant-retroactive", { method: "POST" });
}
export async function setProgramEnabled(enabled) {
  return apiCall("/settings", {
    method: "POST",
    body: JSON.stringify({ programEnabled: enabled }),
  });
}
